import { Color } from '@persona-id/design-tokens';
import { Helmet } from 'react-helmet-async';
import { useRouteMatch } from 'react-router';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ReportDetails from 'dashboard/components/ReportDetails';
import ReportHeader from 'dashboard/components/ReportDetails/ReportHeader';
import { SentryRoute } from 'dashboard/components/SentryRoute/SentryRoute';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import {
  Backdrop,
  Container,
  HeaderContainer,
  MainColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { Report } from 'dashboard/models/report';
import { ReportSnapshot } from 'dashboard/models/report-snapshot';

interface DashboardReportsSnapshotViewProps {
  reportSnapshot: ReportSnapshot;
  isLoading: boolean;
}

const DashboardReportsSnapshotView = ({
  reportSnapshot,
  isLoading,
}: DashboardReportsSnapshotViewProps) => {
  const { path: currentPath } = useRouteMatch();

  if (reportSnapshot == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState message='We couldn’t find that Report.' backRoute='/dashboard/reports' />
      </Backdrop>
    );
  }

  return (
    <>
      <Helmet
        title={`${reportSnapshot.id} - Snapshots - ${reportSnapshot.attributes['report-id']} - Reports`}
      />
      <InactiveEnvironmentBanner />
      <Banner backgroundColor={Color.CoreYellow300}>
        You are viewing a previous run of this report on{' '}
        {formatDate(reportSnapshot.attributes['created-at'])}.
      </Banner>
      <Backdrop>
        <Container>
          <HeaderContainer>
            <ReportHeader report={reportSnapshot} />
          </HeaderContainer>
          <MainColumn>
            <SentryRoute path={currentPath}>
              <ReportDetails report={reportSnapshot as Report} />
            </SentryRoute>
          </MainColumn>
        </Container>
      </Backdrop>
    </>
  );
};

export default DashboardReportsSnapshotView;
